<template>
  <div class="autocomplete-container">
    <div class="input-wrapper">
      <input
        ref="autocompleteInput"
        :class="['autocomplete-input', { error: error, readonly: isInputReadonly }]"
        v-model="inputValue"
        :placeholder="
          isInputReadonly
            ? $t('autoComplete.clickToEdit')
            : placeholder || $t('autoComplete.searchAddress')
        "
        :readonly="isInputReadonly"
        @input="handleInput"
        @keydown="handleKeyDown"
        @focus="onFocus"
        @blur="onBlur"
      />
      <button
        v-if="inputValue"
        type="button"
        class="input-clear-btn"
        @mousedown.prevent
        @click="clearAddress"
        :title="$t('autoComplete.clearAddress')"
      >
        ✕
      </button>
    </div>

    <!-- Dropdown with predictions and custom option -->
    <div v-if="predictions.length > 0 || showCustomOption" class="dropdown-content">
      <div
        v-for="(prediction, index) in predictions"
        :key="prediction.place_id"
        :class="['dropdown-item', { selected: selectedIndex === index }]"
        @click="selectPlace(prediction)"
        @mouseenter="selectedIndex = index"
        @mouseleave="selectedIndex = -1"
      >
        {{ prediction.description }}
      </div>

      <!-- Custom option at the end -->
      <div
        v-if="showCustomOption"
        :class="[
          'dropdown-item',
          'custom-option',
          { selected: selectedIndex === predictions.length },
        ]"
        @click="openCustomAddressModal"
        @mouseenter="selectedIndex = predictions.length"
        @mouseleave="selectedIndex = -1"
      >
        {{ $t('autoComplete.customOptionLabel') }}
      </div>
    </div>

    <!-- Loading state -->
    <div v-else-if="loading" class="dropdown-content">
      <div class="loading-item">{{ $t('autoComplete.searching') }}</div>
    </div>

    <!-- Custom Address Modal -->
    <div
      v-if="showModal"
      class="modal-overlay"
      @mousedown="overlayMouseDown"
      @click="handleOverlayClick"
    >
      <div class="modal-content" @mousedown.stop @click.stop>
        <h3>{{ $t('autoComplete.modalTitle') }}</h3>

        <div class="form-group">
          <label for="postalCode">
            {{ $t('autoComplete.postalCodeLabel') }}
          </label>
          <input
            id="postalCode"
            :value="customAddress.postalCode !== null ? customAddress.postalCode : ''"
            type="text"
            inputmode="numeric"
            :maxlength="requiredPostalCodeLength"
            :placeholder="$t('autoComplete.postalCodePlaceholder')"
            :class="[
              'modal-input',
              { 'input-required': customAddress.postalCode === null || !!postalCodeError },
            ]"
            @input="handlePostalCodeInput($event)"
            @blur="handlePostalCodeBlur"
          />
        </div>
        <span v-if="postalCodeError" class="field-error-msg">{{ postalCodeError }}</span>

        <div class="form-group">
          <label for="street">
            {{ $t('autoComplete.streetLabel') }}
          </label>
          <input
            id="street"
            v-model="customAddress.street"
            type="text"
            :placeholder="$t('autoComplete.streetPlaceholder')"
            :class="['modal-input', { 'input-required': customAddress.street === null }]"
            @input="handleFieldInput('street', $event)"
          />
        </div>

        <div class="form-group">
          <label for="streetNumber">
            {{ $t('autoComplete.streetNumberLabel') }}
          </label>
          <input
            id="streetNumber"
            v-model="customAddress.streetNumber"
            type="text"
            :placeholder="$t('autoComplete.streetNumberPlaceholder')"
            :class="['modal-input', { 'input-required': customAddress.streetNumber === null }]"
            @input="handleFieldInput('streetNumber', $event)"
          />
        </div>

        <div class="form-group">
          <label for="area">
            {{ $t('autoComplete.areaLabel') }}
          </label>
          <input
            id="area"
            v-model="customAddress.area"
            type="text"
            :placeholder="$t('autoComplete.areaPlaceholder')"
            :class="['modal-input', { 'input-required': customAddress.area === null }]"
            @input="handleFieldInput('area', $event)"
          />
        </div>

        <div class="modal-actions">
          <button @click="closeModal" class="btn-cancel">
            {{ $t('autoComplete.cancelButton') }}
          </button>
          <button @click="proceedToMap" class="btn-save" :disabled="isGeocoding">
            {{ $t('autoComplete.nextButton') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast component -->
    <Toast
      v-if="showToast"
      :message="toastMessage"
      :success="toastMessage === $t('autoComplete.addressSaved')"
      @close="closeToast"
      class="toast-above-modal"
    />
  </div>
</template>

<script>
import Toast from '@/components/graphic/Toast.vue';
import { importLibrary } from '@/services/googleMaps';
import { getBadAddressReason, BAD_ADDRESS_REASONS } from '@/services/addressValidation';

export default {
  name: 'AutoCompleteWithPlusCode',

  components: {
    Toast,
  },

  props: {
    defaultValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    error: {
      type: Boolean,
      default: false,
    },
    prefilledAddress: {
      type: Object,
      default: null,
    },
    currentCoords: {
      type: Object,
      default: null,
    },
    hasPinPlaced: {
      type: Boolean,
      default: false,
    },
    mapOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      inputValue: this.defaultValue,
      predictions: [],
      loading: false,
      selectedIndex: -1,
      debounceTimer: null,
      geocoder: null,
      AutocompleteSuggestion: null,
      AutocompleteSessionTokenClass: null,
      Place: null,
      sessionToken: null,
      servicesLocale: null,
      showModal: false,
      isFocused: false,
      showToast: false,
      mouseDownOnOverlay: false,
      toastMessage: '',
      postalCodeError: null,
      isGeocoding: false,
      autocompleteCache: Object.create(null),
      geocodeCache: Object.create(null),
      customAddress: {
        postalCode: null,
        street: null,
        streetNumber: null,
        area: null,
        country: this.$t(
          process.env.VUE_APP_COUNTRY_CODE === 'CY'
            ? 'autoComplete.countryCY'
            : 'autoComplete.countryGR',
        ),
      },
    };
  },

  computed: {
    countryCode() {
      return process.env.VUE_APP_COUNTRY_CODE;
    },

    countryName() {
      const countryMap = {
        CY: this.$t('autoComplete.countryCY'),
        GR: this.$t('autoComplete.countryGR'),
      };
      return countryMap[this.countryCode];
    },

    requiredPostalCodeLength() {
      return process.env.VUE_APP_COUNTRY_CODE === 'CY' ? 4 : 5;
    },

    showCustomOption() {
      const hasAllFields = this.isAddressComplete();

      return (
        !this.mapOpen &&
        !this.loading &&
        !hasAllFields &&
        (this.inputValue.trim().length > 0 ||
          (this.isFocused && this.inputValue.trim().length === 0))
      );
    },

    isInputReadonly() {
      return this.isAddressComplete();
    },
  },

  watch: {
    defaultValue(newValue) {
      this.inputValue = newValue;
    },
    prefilledAddress(val) {
      if (val) {
        this.customAddress = {
          street: val.street,
          streetNumber: val.streetNumber,
          area: val.area,
          postalCode: val.postalCode,
          country: this.countryName,
        };
      } else {
        this.resetCustomAddress();
      }
    },
    '$i18n.locale'() {
      this.predictions = [];
      this.selectedIndex = -1;
      this.resetSessionToken();
    },
  },

  mounted() {
    this.initializeServices();
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    this.cleanup();
  },

  methods: {
    async initializeServices() {
      try {
        const targetLocale = this.$i18n.locale;
        const [placesLib, geocodingLib] = await Promise.all([
          importLibrary('places', targetLocale),
          importLibrary('geocoding', targetLocale),
        ]);
        this.AutocompleteSuggestion = placesLib.AutocompleteSuggestion;
        this.AutocompleteSessionTokenClass = placesLib.AutocompleteSessionToken;
        this.Place = placesLib.Place;
        this.geocoder = new geocodingLib.Geocoder();
        this.servicesLocale = targetLocale;
      } catch (err) {
        console.error('Failed to load Google Maps libraries:', err);
      }
    },

    async ensureServicesForCurrentLocale() {
      if (
        !this.AutocompleteSuggestion ||
        !this.Place ||
        !this.geocoder ||
        this.servicesLocale !== this.$i18n.locale
      ) {
        await this.initializeServices();
      }
    },

    cleanup() {
      document.removeEventListener('click', this.handleClickOutside);
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
    },

    // A session token bundles all keystroke-level Autocomplete calls with the
    // final Place Details call into one billable "session". Without it, every
    // keystroke is billed at the per-request Autocomplete rate.
    ensureSessionToken() {
      if (!this.sessionToken && this.AutocompleteSessionTokenClass) {
        this.sessionToken = new this.AutocompleteSessionTokenClass();
      }
      return this.sessionToken;
    },

    resetSessionToken() {
      this.sessionToken = null;
    },

    isAddressComplete() {
      const { street, streetNumber, area, postalCode, country } = this.customAddress;
      return (
        street !== null &&
        streetNumber !== null &&
        area !== null &&
        postalCode !== null &&
        country !== null
      );
    },

    onFocus() {
      this.isFocused = true;
      if (this.isAddressComplete()) {
        if (this.currentCoords && this.hasPinPlaced) {
          // Second open after a pin was accepted: show boundary circle
          this.$emit('open-map-picker', {
            center: this.currentCoords,
            boundary: { center: this.currentCoords, radius: 100 },
            formattedAddress: this.inputValue,
            customAddressData: { ...this.customAddress },
          });
        } else if (this.currentCoords) {
          // Geocoded address exists but no pin yet: open map free of constraints
          this.$emit('open-map-picker', {
            center: this.currentCoords,
            formattedAddress: this.inputValue,
            customAddressData: { ...this.customAddress },
          });
        } else {
          this.openCustomAddressModal();
        }
      }
    },

    onBlur() {
      setTimeout(() => {
        this.isFocused = false;
      }, 200);
    },

    handleInput() {
      this.selectedIndex = -1;

      if (this.isAddressComplete() && this.inputValue.trim()) {
        this.openCustomAddressModal();
        return;
      }

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        if (this.inputValue.trim()) {
          this.getPlacePredictions(this.inputValue);
        } else {
          this.predictions = [];
        }
      }, 300);

      this.$emit('input-change', this.inputValue);
    },

    handleFieldInput(fieldName, event) {
      const value = event.target.value;

      // If the value is empty or contains only whitespace, set it to null
      if (typeof value === 'string' && value.trim() === '') {
        this.customAddress[fieldName] = null;
      }
    },

    handlePostalCodeInput(event) {
      const raw = event.target.value.replace(' ', '');
      const digitsOnly = raw.replace(/\D/g, '');
      const hadNonDigits = raw !== digitsOnly;
      const clamped = digitsOnly.slice(0, this.requiredPostalCodeLength);
      const exceeded = digitsOnly.length > this.requiredPostalCodeLength;

      // Update DOM immediately to reflect stripped/clamped value
      event.target.value = clamped;
      this.customAddress.postalCode = clamped || null;

      if (hadNonDigits || exceeded) {
        this.postalCodeError = this.$t('autoComplete.errorPostalCodeInvalid', {
          length: this.requiredPostalCodeLength,
        });
      } else {
        this.postalCodeError = null;
      }
    },

    handlePostalCodeBlur() {
      const val = this.customAddress.postalCode?.replace(' ', '');
      if (val && val.length < this.requiredPostalCodeLength) {
        this.postalCodeError = this.$t('autoComplete.errorPostalCodeLength', {
          length: this.requiredPostalCodeLength,
        });
      } else if (!val) {
        this.postalCodeError = null;
      } else {
        this.postalCodeError = null;
      }
    },

    handleKeyDown(event) {
      // Escape is handled before the empty-list guard: dismissing the manual
      // form has to discard the address whether or not a dropdown is showing.
      if (event.keyCode === 27) {
        this.predictions = [];
        if (this.showModal) this.closeModal();
        return;
      }

      const totalItems = this.predictions.length + (this.showCustomOption ? 1 : 0);
      if (totalItems === 0) return;

      switch (event.keyCode) {
        case 40: // Arrow down
          event.preventDefault();
          this.selectedIndex = Math.min(this.selectedIndex + 1, totalItems - 1);
          break;
        case 38: // Arrow up
          event.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          break;
        case 13: // Enter
          event.preventDefault();
          this.handleEnterKey();
          break;
      }
    },

    handleEnterKey() {
      if (this.selectedIndex >= 0) {
        if (this.selectedIndex < this.predictions.length) {
          if (this.isAddressComplete()) {
            this.openCustomAddressModal();
          } else {
            this.selectPlace(this.predictions[this.selectedIndex]);
          }
        } else if (this.showCustomOption) {
          this.openCustomAddressModal();
        }
      }
    },

    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.predictions = [];
      }
    },

    selectPlace(prediction) {
      if (this.isAddressComplete()) {
        this.predictions = [];
        this.selectedIndex = -1;
        this.openCustomAddressModal();
        return;
      }

      this.inputValue = prediction.description;
      this.predictions = [];
      this.selectedIndex = -1;
      this.getPlaceDetails(prediction);
    },

    async getPlacePredictions(input) {
      await this.ensureServicesForCurrentLocale();
      if (!this.AutocompleteSuggestion) return;

      const locale = this.$i18n.locale;
      const cacheKey = `${locale}:${input}`;
      if (this.autocompleteCache[cacheKey]) {
        this.predictions = this.autocompleteCache[cacheKey];
        return;
      }

      this.loading = true;
      try {
        const { suggestions } = await this.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          includedRegionCodes: [this.countryCode.toLowerCase()],
          language: locale,
          sessionToken: this.ensureSessionToken(),
        });
        const list = (suggestions || [])
          .filter(s => s.placePrediction)
          .map(s => ({
            description: s.placePrediction.text.toString(),
            place_id: s.placePrediction.placeId,
            placePrediction: s.placePrediction,
          }));
        this.predictions = list;
        this.autocompleteCache[cacheKey] = list;
      } catch {
        this.predictions = [];
      } finally {
        this.loading = false;
      }
    },

    async getPlaceDetails(prediction) {
      await this.ensureServicesForCurrentLocale();
      if (!this.Place) {
        this.openCustomAddressModal();
        return;
      }

      try {
        // toPlace() carries the session token from the originating Autocomplete
        // call, so the subsequent fetchFields gets bundled into the session.
        const place = prediction.placePrediction
          ? prediction.placePrediction.toPlace()
          : new this.Place({ id: prediction.place_id });
        await place.fetchFields({
          fields: [
            'addressComponents',
            'formattedAddress',
            'location',
            'displayName',
            'id',
            'plusCode',
            'types',
          ],
        });
        // The session ends with the Place Details call.
        this.resetSessionToken();

        this.parseAddressComponents(place);

        const badAddressReason = getBadAddressReason(place, this.countryCode);
        if (badAddressReason) {
          this.rejectAddress(badAddressReason);
          return;
        }

        if (place.location) {
          const center = { lat: place.location.lat(), lng: place.location.lng() };
          this.$emit('open-map-picker', {
            center,
            boundary: { center, radius: 100 },
            formattedAddress: this.inputValue,
            customAddressData: { ...this.customAddress },
          });
        } else {
          this.openCustomAddressModal();
        }
      } catch {
        this.openCustomAddressModal();
      }
    },

    parseAddressComponents(place) {
      const components = place.addressComponents || [];
      const result = {
        streetNumber: '',
        route: '',
        locality: '',
        administrativeAreaLevel1: '',
        country: '',
        countryCode: '',
        postalCode: '',
        subpremise: '',
      };

      components.forEach(component => {
        const types = component.types;
        const longName = component.longText;
        const shortName = component.shortText;

        if (types.includes('street_number')) result.streetNumber = longName;
        if (types.includes('route')) result.route = longName;
        if (types.includes('locality')) result.locality = longName;
        if (types.includes('administrative_area_level_1'))
          result.administrativeAreaLevel1 = longName;
        if (types.includes('country')) {
          result.country = longName;
          result.countryCode = shortName;
        }
        if (types.includes('postal_code')) result.postalCode = longName;
        if (types.includes('subpremise')) result.subpremise = longName;
      });

      // Update customAddress with found values
      this.updateCustomAddressFromResult(result);

      return result;
    },

    updateCustomAddressFromResult(result) {
      const updatedCustomAddress = { ...this.customAddress };

      if (result.postalCode) updatedCustomAddress.postalCode = result.postalCode;
      if (result.route) updatedCustomAddress.street = result.route;
      if (result.streetNumber) updatedCustomAddress.streetNumber = result.streetNumber;
      if (result.locality || result.administrativeAreaLevel1) {
        updatedCustomAddress.area = result.locality || result.administrativeAreaLevel1;
      }

      updatedCustomAddress.country = this.countryName;
      this.customAddress = updatedCustomAddress;
    },

    formatCustomAddress() {
      const parts = [];

      // Street part
      const streetPart = this.buildCustomStreetPart();
      if (streetPart) parts.push(streetPart);

      // Area part
      const areaPart = this.buildCustomAreaPart();
      if (areaPart) parts.push(areaPart);

      // Country part
      parts.push(this.customAddress.country);

      return parts.join(', ');
    },

    buildCustomStreetPart() {
      if (!this.customAddress.street && !this.customAddress.streetNumber) return '';
      return `${this.customAddress.street || ''} ${this.customAddress.streetNumber || ''}`.trim();
    },

    buildCustomAreaPart() {
      if (!this.customAddress.area && !this.customAddress.postalCode) return '';
      return `${this.customAddress.area || ''} ${this.customAddress.postalCode || ''}`.trim();
    },

    openCustomAddressModal() {
      this.predictions = [];
      this.selectedIndex = -1;
      this.showModal = true;

      // Reset if completely empty
      const shouldReset =
        !this.customAddress.street &&
        !this.customAddress.area &&
        !this.customAddress.postalCode &&
        !this.customAddress.streetNumber;

      if (shouldReset) {
        this.resetCustomAddress();
      }
    },

    rejectAddress(reason) {
      if (reason === BAD_ADDRESS_REASONS.WRONG_COUNTRY) {
        this.resetCustomAddress();
        this.showToastMessage(
          this.$t('autoComplete.errorWrongCountry', { country: this.countryName }),
        );
      } else if (reason === BAD_ADDRESS_REASONS.MISSING_STREET_NUMBER) {
        this.showToastMessage(this.$t('autoComplete.errorMissingStreetNumber'));
      } else {
        this.showToastMessage(this.$t('autoComplete.errorImpreciseAddress'));
      }

      this.openCustomAddressModal();
    },

    clearAddress() {
      this.inputValue = '';
      this.predictions = [];
      this.selectedIndex = -1;
      this.showModal = false;
      this.resetCustomAddress();
      this.resetSessionToken();
      this.$emit('input-change', '');
    },

    overlayMouseDown() {
      this.mouseDownOnOverlay = true;
    },

    handleOverlayClick() {
      if (this.mouseDownOnOverlay) {
        this.closeModal();
      }
      this.mouseDownOnOverlay = false;
    },

    closeModal() {
      this.showModal = false;
      this.resetCustomAddress();
      this.resetSessionToken();
      this.inputValue = '';
      this.$emit('input-change', '');
    },

    resetCustomAddress() {
      this.customAddress = {
        postalCode: null,
        street: null,
        streetNumber: null,
        area: null,
        country: this.countryName,
      };
    },

    async proceedToMap() {
      if (this.isGeocoding) return;

      const validationErrors = this.validateCustomAddress();
      if (validationErrors.length > 0) {
        this.showToastMessage(validationErrors[0]);
        return;
      }

      this.isGeocoding = true;
      try {
        await this.ensureServicesForCurrentLocale();

        let center = null;
        let result = null;
        if (this.geocoder) {
          const formattedAddress = this.formatCustomAddress();
          result = await this.geocodeAddress(formattedAddress);
          center = result?.geometry?.location
            ? { lat: result.geometry.location.lat(), lng: result.geometry.location.lng() }
            : null;
        }

        const resolvedCountryCode = this.extractCountryCode(result);
        if (resolvedCountryCode && resolvedCountryCode !== this.countryCode) {
          this.showToastMessage(
            this.$t('autoComplete.errorWrongCountry', { country: this.countryName }),
          );
          return;
        }

        this.showModal = false;
        this.$emit('open-map-picker', {
          center,
          boundary: center && this.isExactGeocodeMatch(result) ? { center, radius: 100 } : null,
          source: 'manual',
          formattedAddress: this.formatCustomAddress(),
          customAddressData: { ...this.customAddress },
        });
      } finally {
        this.isGeocoding = false;
      }
    },

    geocodeAddress(address) {
      const locale = this.$i18n.locale;
      const cacheKey = `${locale}:${address}`;
      if (this.geocodeCache[cacheKey]) {
        return Promise.resolve(this.geocodeCache[cacheKey]);
      }
      return new Promise(resolve => {
        this.geocoder.geocode({ address, language: locale }, (results, status) => {
          const result = status === 'OK' && results[0] ? results[0] : null;
          if (result) this.geocodeCache[cacheKey] = result;
          resolve(result);
        });
      });
    },

    extractCountryCode(geocodeResult) {
      const country = geocodeResult?.address_components?.find(c => c.types.includes('country'));
      return country?.short_name || null;
    },

    isExactGeocodeMatch(result) {
      if (!result || result.partial_match) return false;
      const locationType = result.geometry?.location_type;
      if (locationType !== 'ROOFTOP' && locationType !== 'RANGE_INTERPOLATED') return false;
      const geocodedTk = result.address_components?.find(c => c.types.includes('postal_code'))
        ?.long_name;
      if (!geocodedTk || !this.customAddress.postalCode) return false;
      return geocodedTk.replace(/\s/g, '') === this.customAddress.postalCode.replace(/\s/g, '');
    },

    validateCustomAddress() {
      const errors = [];
      const requiredFields = [
        {
          field: 'postalCode',
          message: this.$t('autoComplete.errorPostalCode'),
        },
        { field: 'street', message: this.$t('autoComplete.errorStreet') },
        {
          field: 'streetNumber',
          message: this.$t('autoComplete.errorStreetNumber'),
        },
        { field: 'area', message: this.$t('autoComplete.errorArea') },
      ];

      requiredFields.forEach(({ field, message }) => {
        if (this.customAddress[field] === null) {
          errors.push(message);
        }
      });

      if (
        this.customAddress.postalCode !== null &&
        this.customAddress.postalCode.replace(' ', '').length !== this.requiredPostalCodeLength
      ) {
        errors.push(
          this.$t('autoComplete.errorPostalCodeLength', { length: this.requiredPostalCodeLength }),
        );
        this.postalCodeError = this.$t('autoComplete.errorPostalCodeLength', {
          length: this.requiredPostalCodeLength,
        });
      }

      return errors;
    },

    showToastMessage(message) {
      this.showToast = false;
      this.toastMessage = '';

      this.$nextTick(() => {
        this.toastMessage = message;
        this.showToast = true;

        setTimeout(() => {
          this.showToast = false;
          this.toastMessage = '';
        }, 3000);
      });
    },

    closeToast() {
      this.showToast = false;
      this.toastMessage = '';
    },
  },
};
</script>

<style scoped>
.autocomplete-container {
  position: relative;
  width: 100%;
}

.input-wrapper {
  position: relative;
  margin-bottom: 15px;
}

.autocomplete-input {
  padding: 12px 34px 12px 12px;
  margin-bottom: 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
  font-size: 1rem;
  background-color: white;
  color: #333;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.input-clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #aaa;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  padding: 2px 4px;
  width: auto;
  margin: 0;
}

.input-clear-btn:hover {
  color: #666;
  background: none;
}

.autocomplete-input.error {
  border-color: #e74c3c;
  box-shadow: 0 0 5px rgba(231, 76, 60, 0.3);
}

.autocomplete-input.readonly {
  background-color: #f8f9fa;
  cursor: pointer;
}

.autocomplete-input.readonly:hover {
  background-color: #e9ecef;
}

.autocomplete-input:focus {
  border-color: #3498db;
  outline: none;
  box-shadow: 0 0 8px rgba(52, 152, 219, 0.3);
}

.dropdown-content {
  position: absolute;
  top: calc(100% - 15px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.95rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;
  line-height: 1.4;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
  color: #2c3e50;
}

.dropdown-item.selected {
  background-color: #e9ecef;
  color: #495057;
}

.dropdown-item:last-child {
  border-bottom: none;
  border-radius: 0 0 4px 4px;
}

.dropdown-item:first-child {
  border-radius: 4px 4px 0 0;
}

.loading-item {
  padding: 16px;
  color: #6c757d;
  text-align: center;
  font-style: italic;
  background: white;
  font-size: 0.9rem;
}

.custom-option {
  font-style: italic;
  color: #6c757d;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.custom-option:hover {
  background-color: #e9ecef;
  color: #495057;
}

.custom-option.selected {
  background-color: #dee2e6;
  color: #495057;
}

/* Scroll styling */
.dropdown-content::-webkit-scrollbar {
  width: 6px;
}

.dropdown-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.dropdown-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.dropdown-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 10001;
}

.modal-content h3 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 1.2rem;
  text-align: center;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  color: #555;
  font-size: 0.9rem;
}

.modal-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  box-sizing: border-box;
  transition: border-color 0.3s ease;
}

.modal-input.input-required {
  border-color: #e74c3c;
  box-shadow: 0 0 5px rgba(231, 76, 60, 0.3);
}

.field-error-msg {
  display: block;
  margin-top: -10px;
  margin-bottom: 8px;
  font-size: 0.8rem;
  color: #e74c3c;
}

.modal-input.input-required:focus {
  outline: none;
  border-color: #e74c3c;
  box-shadow: 0 0 8px rgba(231, 76, 60, 0.4);
}

.modal-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 5px rgba(52, 152, 219, 0.3);
}

.modal-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.btn-cancel,
.btn-save {
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.btn-cancel {
  background-color: #6c757d;
  color: white;
}

.btn-cancel:hover {
  background-color: #5a6268;
}

.btn-save {
  background-color: #28a745;
  color: white;
}

.btn-save:hover {
  background-color: #218838;
}

/* Toast positioning */
.toast-above-modal {
  z-index: 10010 !important;
  position: fixed !important;
}

:deep(.toast-container) {
  z-index: 10010 !important;
  position: fixed !important;
}

:deep(.toast) {
  z-index: 10010 !important;
}

/* Mobile responsive */
@media only screen and (max-width: 600px) {
  .autocomplete-input {
    padding: 10px;
    font-size: 0.9rem;
  }

  .dropdown-item {
    padding: 10px 12px;
    font-size: 0.85rem;
  }

  .loading-item {
    padding: 12px;
    font-size: 0.85rem;
  }

  .modal-content {
    margin: 20px;
    padding: 20px;
  }

  .modal-input {
    padding: 8px 10px;
    font-size: 0.9rem;
  }

  .btn-cancel,
  .btn-save {
    padding: 8px 12px;
    font-size: 0.9rem;
  }
}
</style>
